import type { QueryClient } from '@tanstack/react-query'
import { api } from '@/lib/api-client.ts'
import type { StageGuideT } from '@/help/guide/stageGuideTypes.ts'
import type { GuideStepKeyT } from '@/help/hints/guideSteps.ts'

/**
 * Keyed by user as well as stage: the track and `applicable` belong to the viewer, and
 * successful queries are persisted to IndexedDB, so a stage-only key could hand one user's
 * guide to whoever signs in next on the device.
 */
export const STAGE_GUIDE_QUERY_ROOT = 'stageGuide'

export const stageGuideUserQueryKey = (userId: number) => [STAGE_GUIDE_QUERY_ROOT, userId] as const

export const stageGuideQueryKey = (userId: number, stageId: string | number) =>
  [...stageGuideUserQueryKey(userId), String(stageId)] as const

export const getStageGuideQuery = (userId: number, stageId: string) => ({
  queryKey: stageGuideQueryKey(userId, stageId),
  queryFn: () => getStageGuide(stageId),
  staleTime: 1000 * 30,
})

export async function getStageGuide(stageId: string): Promise<StageGuideT> {
  const response = await api.get<StageGuideT>(`/project_stages/${stageId}/guide`)
  return response.data
}

/**
 * Asks every cached guide again after a write that can change a step. The guide is not
 * refreshed by time: the app's default staleTime only marks data stale, and refetch on window
 * focus is off, so without this a finished step would stay "to do" until the next mount.
 *
 * Call it only after a response the server actually produced. A queued POST/PATCH never gets
 * here — `api-client.ts` rejects it with a NativeQueuedResponse carrying a mock 202 — and the
 * writes this is wired to (people, companies, review marks) are not queueable anyway
 * (`queueBypass.ts`). Refetching after a mock 202 would read a guide the server has not
 * updated yet ("CRITICAL: Mercure Mutation Pattern" in CLAUDE.md).
 */
export function invalidateStageGuides(queryClient: QueryClient) {
  return queryClient.invalidateQueries({ queryKey: [STAGE_GUIDE_QUERY_ROOT] })
}

/**
 * Asks one stage's guide again after a Mercure event that may complete `step` (a new issue or
 * comment on the stage) — but only while the cached guide still has that step open. Once it is
 * done no event can change it back to "to do" that matters for a hint, so a busy stage does
 * not refetch the guide on every broadcast. Without a cached guide nobody is showing a hint.
 *
 * Mercure events come from the server after its commit, so this never reads a guide ahead of
 * the data — unlike a refetch after the mock 202 of a queued write ("CRITICAL: Mercure
 * Mutation Pattern" in CLAUDE.md), which this must not be wired to.
 */
export function invalidateStageGuideWhileStepOpen(
  queryClient: QueryClient,
  userId: number | null | undefined,
  stageId: string | number | null | undefined,
  step: GuideStepKeyT,
) {
  if (userId == null || stageId == null || stageId === '') return
  const queryKey = stageGuideQueryKey(userId, stageId)
  const guide = queryClient.getQueryData<StageGuideT>(queryKey)
  const open = guide?.steps.some(s => s.key === step && s.applicable && !s.done)
  if (!open) return
  void queryClient.invalidateQueries({ queryKey, exact: true })
}
